import React from "react";
import { motion } from "framer-motion";

type LogoItem = {
  name: string;
  image?: string;
};

type DivisionClientsStripProps = {
  title?: string;
  desc?: string;
  clientsText?: string;
  logoUrls?: string;
  logoImages?: Array<string | undefined>;
};

const splitLines = (text?: string) =>
  (text || "")
    .split(/\r?\n|;/)
    .map((line) => line.trim())
    .filter(Boolean);

const normalizeImage = (image?: string) => {
  if (!image) return "";
  if (image.startsWith("http") || image.startsWith("data:") || image.startsWith("blob:")) return image;
  if (image.startsWith("/")) return `https://hamdanoman.com${image}`;
  return image;
};

const initials = (name: string) => {
  const value = name
    .replace(/&/g, " ")
    .split(/\s+/)
    .map((word) => word.replace(/[^a-zA-Z0-9]/g, ""))
    .filter(Boolean)
    .slice(0, 3)
    .map((word) => word[0])
    .join("");

  return value || name.slice(0, 2).toUpperCase();
};

const formatClientName = (name: string) =>
  name
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

const normalizeName = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]/g, "");

const buildLogoItems = ({ clientsText, logoUrls, logoImages }: DivisionClientsStripProps) => {
  const clientNames = splitLines(clientsText).map((line) => {
    const [name] = line.split("|").map((part) => part.trim()).filter(Boolean);
    return name || line;
  });

  const items: LogoItem[] = clientNames.map((name) => ({ name }));
  const itemIndexByName = new Map(
    items.map((item, index) => [normalizeName(item.name), index])
  );

  (logoImages || []).map(normalizeImage).forEach((image, index) => {
    if (!image) return;
    if (items[index]) {
      items[index] = { ...items[index], image };
    } else {
      items.push({ name: `Client ${index + 1}`, image });
    }
  });

  splitLines(logoUrls).forEach((line, index) => {
    const parts = line.split("|").map((part) => part.trim()).filter(Boolean);
    const hasName = parts.length > 1;
    const name = hasName ? parts[0] : clientNames[index] || `Client ${index + 1}`;
    const image = normalizeImage(hasName ? parts[1] : parts[0]);

    if (!image) return;

    const existingIndex = itemIndexByName.get(normalizeName(name));
    if (existingIndex !== undefined) {
      items[existingIndex] = { ...items[existingIndex], image };
      return;
    }

    items.push({ name, image });
    itemIndexByName.set(normalizeName(name), items.length - 1);
  });

  return items;
};

const DivisionClientsStrip: React.FC<DivisionClientsStripProps> = (props) => {
  const items = buildLogoItems(props);
  if (items.length === 0) return null;

  const duplicated = [...items, ...items];
  const title = props.title || "Our Clients";
  const desc = props.desc || "Trusted clients across Hamdan Group divisions and operations in Oman.";

  return (
    <section className="bg-white py-8 overflow-hidden">
      <style>{`
        @keyframes divisionClientLogoMarquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .division-client-card-track {
          width: max-content;
          animation: divisionClientLogoMarquee 90s linear infinite;
        }

        .division-client-card-stage:hover .division-client-card-track {
          animation-play-state: paused;
        }
      `}</style>

      <div className="mx-auto max-w-7xl px-4">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="text-center"
        >
          <h2 className="text-3xl font-bold text-black md:text-4xl">{title}</h2>
          <div className="mx-auto mt-3 h-1 w-24 rounded-full bg-[#F2BB13]" />
          {desc && (
            <p className="mx-auto mt-8 max-w-4xl text-center text-base leading-relaxed text-black md:text-lg">
              {desc}
            </p>
          )}
        </motion.div>

        <div className="division-client-card-stage relative mt-9">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-r from-white via-white/80 to-transparent sm:w-36" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-white via-white/80 to-transparent sm:w-36" />

          <div className="overflow-hidden py-4">
            <div className="division-client-card-track flex items-center gap-8 md:gap-12">
              {duplicated.map((item: LogoItem, index: number) => (
                <div
                  key={`${item.name}-${index}`}
                  className="flex h-28 w-[285px] shrink-0 items-center gap-4 rounded-lg border border-slate-100 bg-white px-5 shadow-[0_10px_28px_rgba(15,23,42,0.06)]"
                >
                  <div className="flex h-24 w-24 shrink-0 items-center justify-center">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="max-h-24 max-w-24 object-contain"
                      />
                    ) : (
                      <span className="text-base font-black uppercase leading-none text-[#D32F2F]">
                        {initials(item.name)}
                      </span>
                    )}
                  </div>
                  <h3 className="min-w-0 flex-1 text-center text-sm font-black uppercase leading-snug text-slate-700 md:text-[15px]">
                    {formatClientName(item.name)}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DivisionClientsStrip;
